
import React, { useState, useEffect } from 'react';
import { Menu, Globe, Phone, Mail, X, Pencil, Save, RotateCcw, Check, Loader2, AlertTriangle, Facebook, Instagram, LogOut, MessageCircle, ArrowUp, QrCode, ShieldCheck, ExternalLink, Copy, MapPin, Play, Download, ChevronDown, RefreshCw, UploadCloud, Home, Grid, Sparkles, User, ShoppingBag, Newspaper, HelpCircle, Calculator, Eraser, Database, CloudOff, Info, WifiOff, Settings, ShieldAlert, ChevronRight, MessageSquare } from 'lucide-react';
import { Language, Translations, Page } from '../types';
import { useEditor } from '../contexts/EditorContext';
import { useAuth } from '../contexts/AuthContext';
import { EditableText } from './EditableText';
import { LoginModal } from './LoginModal';
import { GeminiAdvisor } from './GeminiAdvisor';

const WeChatIcon = ({ size = 20, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className}>
    <path d="M8.22 1c-4.14 0-7.5 2.86-7.5 6.4c0 1.93.98 3.66 2.5 4.88l-.65 2.37l2.5-1.25c.37.08.75.12 1.15.12c4.14 0 7.5-2.86 7.5-6.4S12.36 1 8.22 1zm1.75 3.3a.9.9 0 1 1 0 1.8a.9.9 0 0 1 0-1.8zm-3.5 0a.9.9 0 1 1 0 1.8a.9.9 0 0 1 0-1.8zm11.3 4c-3.45 0-6.25 2.38-6.25 5.32c0 2.94 2.8 5.33 6.25 5.33c.33 0 .65-.03.96-.1l2.08 1.05l-.54-1.98a4.06 4.06 0 0 0 2.08-3.32C22.5 10.68 19.72 8.3 16.27 8.3zm1.46 2.75a.75.75 0 1 1 0 1.5a.75.75 0 0 1 0-1.5zm-2.92 0a.75.75 0 1 1 0 1.5a.75.75 0 0 1 0-1.5z"/>
  </svg>
);

interface LayoutProps {
  children: React.ReactNode;
  currentLang: Language;
  setLanguage: (lang: Language) => void;
  currentPage: Page;
  setPage: (page: Page) => void;
  t: Translations[Language];
}

const LANGUAGE_LABELS: Record<Language, string> = {
  [Language.EN]: 'English',
  [Language.AR]: 'العربية (Arabic)',
  [Language.FR]: 'Français (French)',
  [Language.ZH]: '中文 (Chinese)',
  [Language.RU]: 'Русский (Russian)'
};

const NAV_ITEMS = [
  { key: 'home', label: 'nav.home' },
  { key: 'products', label: 'nav.products' },
  { key: 'cases', label: 'nav.cases' },
  { key: 'resources', label: 'nav.resources' }, 
  { key: 'about', label: 'nav.about' },
  { key: 'contact', label: 'nav.contact' },
];

export const Layout: React.FC<LayoutProps> = ({ children, currentLang, setLanguage, currentPage, setPage, t }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const { isEditMode, toggleEditMode, saveChanges, refreshData, hasUnsavedChanges, saveStatus, dataDescription } = useEditor();
  const { isAuthenticated, isAdminUIEnabled } = useAuth();

  const isRTL = currentLang === Language.AR;
  
  useEffect(() => {
    document.documentElement.lang = currentLang;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [currentLang, isRTL]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsApp = () => {
    const whatsapp = t.contactInfo?.whatsapp || "+86 13149898413";
    const phone = whatsapp.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className={`min-h-screen flex flex-col bg-slate-50 transition-colors duration-300 ${isRTL ? 'font-arabic' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* 编辑模式顶部条 */}
      {isAdminUIEnabled && (
        <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between z-[70] sticky top-0 border-b border-white/10 shadow-2xl">
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
                <Database size={14} className="text-primary-400" />
                <span className="text-[10px] font-black uppercase tracking-widest">{dataDescription}</span>
             </div>
             {hasUnsavedChanges && <span className="flex items-center gap-1.5 text-[10px] font-black text-amber-400 uppercase animate-pulse"><AlertTriangle size={12}/> Unsaved changes</span>}
          </div>
          <div className="flex items-center gap-2">
             <button onClick={refreshData} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Reload from Cloud"><RefreshCw size={16}/></button>
             <button onClick={toggleEditMode} className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${isEditMode ? 'bg-primary-600 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                {isEditMode ? <Check size={14}/> : <Pencil size={14}/>} {isEditMode ? 'Finish Editing' : 'Edit Mode'}
             </button>
             {isEditMode && (
                <button onClick={saveChanges} disabled={saveStatus === 'saving'} className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20">
                   {saveStatus === 'saving' ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save to Cloud
                </button>
             )}
          </div>
        </div>
      )}

      {/* 浮动工具栏 */}
      <div className={`fixed ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 z-[60] flex flex-col gap-3 items-end pointer-events-none pr-0`}>
          <button onClick={handleWhatsApp} className={`pointer-events-auto bg-[#25D366] text-white p-3.5 ${isRTL ? 'rounded-r-2xl border-l-4 border-white/20' : 'rounded-l-2xl border-r-4 border-white/20'} shadow-2xl hover:translate-x-[-10px] transition-all duration-300 group relative flex items-center gap-3 w-14 hover:w-44 overflow-hidden`}>
             <MessageCircle size={24} className="shrink-0" />
             <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[11px] font-black uppercase tracking-widest">WhatsApp</span>
          </button>
          <div className="relative group pointer-events-auto">
              <button className={`bg-[#07C160] text-white p-3.5 ${isRTL ? 'rounded-r-2xl border-l-4 border-white/20' : 'rounded-l-2xl border-r-4 border-white/20'} shadow-2xl flex items-center gap-3 w-14 hover:w-44 transition-all duration-300 group-hover:translate-x-[-10px] overflow-hidden`}>
                  <WeChatIcon size={24} className="shrink-0" />
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[11px] font-black uppercase tracking-widest">WeChat ID</span>
              </button>
              <div className={`absolute ${isRTL ? 'left-full ml-4' : 'right-full mr-4'} top-[-80px] opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-300 origin-right`}>
                  <div className="bg-white p-5 rounded-[2.5rem] shadow-2xl border border-slate-100 flex flex-col items-center ring-[12px] ring-white/60 backdrop-blur-md min-w-[210px]">
                      <img src={t.contactInfo?.wechatQr || "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=wxid_meitai"} alt="WeChat Contact QR Code - Coriolis Mass Flowmeter Support" className="w-36 h-36 rounded-lg object-contain bg-slate-50 p-2" />
                      <p className="mt-3 text-[10px] font-black text-slate-800 uppercase tracking-widest">Scan for WeChat</p>
                  </div>
              </div>
          </div>
          <button onClick={() => setPage('contact')} className={`pointer-events-auto bg-primary-600 text-white p-3.5 ${isRTL ? 'rounded-r-2xl border-l-4 border-white/20' : 'rounded-l-2xl border-r-4 border-white/20'} shadow-2xl hover:translate-x-[-10px] transition-all duration-300 group relative flex items-center gap-3 w-14 hover:w-44 overflow-hidden`}>
             <MessageSquare size={24} className="shrink-0" />
             <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[11px] font-black uppercase tracking-widest">Inquiry</span>
          </button>
          <button onClick={scrollToTop} className={`pointer-events-auto bg-slate-900 text-white p-3.5 ${isRTL ? 'rounded-r-2xl' : 'rounded-l-2xl'} shadow-2xl transition-all duration-500 w-14 flex items-center justify-center ${showBackToTop ? 'translate-x-0 opacity-100' : (isRTL ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0')}`}>
            <ArrowUp size={24} />
          </button>
      </div>

      {/* 顶部导航 */}
      <header className={`sticky ${isAdminUIEnabled ? 'top-[45px]' : 'top-0'} z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-md py-2' : 'bg-white/90 backdrop-blur py-3 lg:py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12 lg:h-16">
            <div className="flex-shrink-0 flex items-center cursor-pointer gap-3" onClick={() => setPage('home')}>
              <div className="h-10 w-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg"><span className="text-white font-black text-xl font-display">M</span></div>
              <span className="text-xl lg:text-2xl font-black text-secondary-900 font-display flex flex-col leading-none">
                <span>MEITAI</span>
                <span className="text-[8px] text-primary-600 font-bold tracking-[0.3em] uppercase pl-0.5">Instruments</span>
              </span>
            </div>
            <nav className="hidden lg:flex gap-1 items-center">
              {NAV_ITEMS.map((item) => (
                <button key={item.key} onClick={() => setPage(item.key as Page)} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${currentPage === item.key ? 'text-primary-600 bg-primary-50' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <EditableText path={item.label} lang={currentLang} />
                </button>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-4">
              <div className="relative">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-2 text-slate-700 hover:text-primary-600 font-bold px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all border border-slate-200">
                  <Globe size={18} className="text-primary-500" />
                  <span>{LANGUAGE_LABELS[currentLang]}</span>
                  <ChevronDown size={14} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {isLangMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="px-4 pb-2 mb-2 border-b border-slate-50"><span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Select Language</span></div>
                    {Object.values(Language).map(lang => (
                        <button key={lang} onClick={() => { setLanguage(lang); setIsLangMenuOpen(false); }} className={`block w-full text-left px-5 py-2.5 text-sm font-bold transition-colors ${currentLang === lang ? 'text-primary-600 bg-primary-50 border-r-4 border-primary-500' : 'text-slate-600 hover:bg-slate-50'}`}>{LANGUAGE_LABELS[lang]}</button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setPage('contact')} className="bg-primary-600 text-white px-7 py-3 rounded-full text-sm font-black shadow-xl hover:bg-primary-700 transition-all active:scale-95">
                <EditableText path="common.viewAll" lang={currentLang} />
              </button>
            </div>
            <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2 text-slate-600"><Menu size={28} /></button>
          </div>
        </div>
      </header>

      {/* 移动端菜单 */}
      <div className={`fixed inset-0 z-[100] lg:hidden transition-all duration-500 ${isMobileMenuOpen ? 'visible' : 'invisible'}`}>
          <div className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className={`absolute right-0 top-0 bottom-0 w-[80%] bg-white shadow-2xl transition-transform duration-500 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
              <div className="p-6 border-b flex justify-between items-center">
                  <span className="font-black text-slate-900 uppercase">Menu</span>
                  <button onClick={() => setIsMobileMenuOpen(false)}><X size={24}/></button>
              </div>
              <div className="p-6 space-y-2">
                  {NAV_ITEMS.map((item) => (
                    <button key={item.key} onClick={() => { setPage(item.key as Page); setIsMobileMenuOpen(false); }} className={`w-full text-left p-4 rounded-2xl font-black ${currentPage === item.key ? 'bg-primary-50 text-primary-600' : 'text-slate-600'}`}>
                      <EditableText path={item.label} lang={currentLang} />
                    </button>
                  ))}
                  <div className="pt-6 border-t mt-6">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Preferred Language</p>
                      <div className="grid grid-cols-1 gap-2">
                          {Object.values(Language).map(lang => (
                              <button key={lang} onClick={() => { setLanguage(lang); setIsMobileMenuOpen(false); }} className={`w-full text-left px-5 py-4 rounded-xl text-sm font-bold border ${currentLang === lang ? 'bg-primary-600 text-white border-primary-600' : 'bg-slate-50 border-slate-100'}`}>{LANGUAGE_LABELS[lang]}</button>
                          ))}
                      </div>
                  </div>
              </div>
          </div>
      </div>

      <main className="flex-grow">{children}</main>

      {/* 底部导航栏 */}
      <footer className="bg-secondary-900 text-slate-400 pt-20 pb-12 mt-auto border-t border-white/5 min-h-[400px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            
            {/* 品牌与简介 */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="h-10 w-10 bg-primary-600 rounded-xl flex items-center justify-center font-display font-black text-white text-xl">M</div>
                <span className="text-2xl font-display font-bold text-white tracking-tight uppercase">MEITAI</span>
              </div>
              <p className="text-sm leading-relaxed mb-8 max-w-xs"><EditableText path="footer.companyDesc" lang={currentLang} multiline={true} /></p>
              <div className="flex gap-4">
                 {[Facebook, Instagram, MessageCircle].map((Icon, i) => (
                    <button key={i} className="p-2.5 bg-white/5 hover:bg-primary-600 text-slate-400 hover:text-white rounded-xl transition-all"><Icon size={20} /></button>
                 ))}
              </div>
            </div>

            {/* 产品分类 */}
            <div className="text-center md:text-left">
               <h4 className="text-white font-black uppercase tracking-widest text-[11px] mb-8 border-b border-white/10 pb-4 inline-block"><EditableText path="nav.products" lang={currentLang} /></h4>
               <ul className="space-y-4 text-sm font-bold">
                  {['pressure', 'temp', 'level', 'flow', 'analysis'].map(cat => (
                    <li key={cat}><button onClick={() => setPage('products')} className="hover:text-primary-400 transition-colors flex items-center gap-2 justify-center md:justify-start group"><ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-all" /><EditableText path={`footer.categories.${cat}`} lang={currentLang} /></button></li>
                  ))}
               </ul>
            </div>

            {/* 全球市场 */}
            <div className="text-center md:text-left">
               <h4 className="text-white font-black uppercase tracking-widest text-[11px] mb-8 border-b border-white/10 pb-4 inline-block"><EditableText path="footer.marketsTitle" lang={currentLang} /></h4>
               <ul className="space-y-4 text-sm font-bold">
                  <li><EditableText path="footer.market1" lang={currentLang} /></li>
                  <li><EditableText path="footer.market2" lang={currentLang} /></li>
                  <li><EditableText path="footer.market3" lang={currentLang} /></li>
                  <li className="pt-2">
                     <span className="text-primary-500 font-black text-[10px] uppercase tracking-widest block mb-1">Global HQ</span>
                     <div className="flex items-center gap-2 justify-center md:justify-start text-xs"><MapPin size={14} /><EditableText path="footer.address" lang={currentLang} /></div>
                  </li>
               </ul>
            </div>

            {/* 联系我们 */}
            <div className="text-center md:text-left">
               <h4 className="text-white font-black uppercase tracking-widest text-[11px] mb-8 border-b border-white/10 pb-4 inline-block"><EditableText path="footer.contactTitle" lang={currentLang} /></h4>
               <div className="space-y-6">
                  <a href={`mailto:${t.footer?.email || 'meitaicekong@126.com'}`} className="flex items-center justify-center md:justify-start gap-3 group">
                     <div className="p-2.5 bg-white/5 rounded-xl group-hover:bg-primary-600/20 transition-colors border border-white/5"><Mail size={18} className="text-primary-500" /></div>
                     <span className="text-sm font-black group-hover:text-white transition-colors">{t.footer?.email || 'meitaicekong@126.com'}</span>
                  </a>
                  <a href={`tel:${t.footer?.phone || '+86 13149898413'}`} className="flex items-center justify-center md:justify-start gap-3 group">
                     <div className="p-2.5 bg-white/5 rounded-xl group-hover:bg-primary-600/20 transition-colors border border-white/5"><Phone size={18} className="text-primary-500" /></div>
                     <span className="text-sm font-black group-hover:text-white transition-colors">{t.footer?.phone || '+86 13149898413'}</span>
                  </a>
                  <button onClick={() => setPage('contact')} className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-2xl transition-all">Inquire Now</button>
               </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] font-black uppercase tracking-[0.2em] opacity-40">
            <p><EditableText path="footer.copyright" lang={currentLang} /></p>
            <div className="flex gap-8">
               <button className="hover:text-white">Privacy Policy</button>
               <button className="hover:text-white">Terms of Export</button>
            </div>
          </div>
        </div>
      </footer>
      <GeminiAdvisor currentLang={currentLang} t={t} />
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} onSuccess={() => {}} />
    </div>
  );
};
